import React from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    TextField,
    Typography,
    Checkbox,
    FormControlLabel,
    Paper,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useEffect } from "react";
import { FRONTEND_MESSAGES } from "../../utils/messages";

const { VALIDATION } = FRONTEND_MESSAGES;

const validationSchema = Yup.object({
    price: Yup.number()
        .min(0, VALIDATION.POSITIVE_ONLY("Price"))
        .nullable(),
    userLimit: Yup.number()
        .integer(VALIDATION.NO_DECIMAL("User limit"))
        .min(0, VALIDATION.POSITIVE_ONLY("User limit"))
        .max(999999, VALIDATION.MAX_VALUE("User limit", 999999))
        .nullable(),
    messageLimit: Yup.number()
        .integer(VALIDATION.NO_DECIMAL("Message limit"))
        .min(0, VALIDATION.POSITIVE_ONLY("Message limit"))
        .max(999999, VALIDATION.MAX_VALUE("Message limit", 999999))
        .nullable(),
    agentLimit: Yup.number()
        .integer(VALIDATION.NO_DECIMAL("Agent limit"))
        .min(0, VALIDATION.POSITIVE_ONLY("Agent limit"))
        .max(999999, VALIDATION.MAX_VALUE("Agent limit", 999999))
        .nullable(),
});

const CustomPlanPopup = ({
    open,
    onClose,
    billingCycleLabel,
    currencySymbol,
    priceValue,
    planDefaultPrice,
    noPlanSelected = false,
    initialLimits = {},
    onSave,
}) => {
    const [userUnlimited, setUserUnlimited] = React.useState(false);
    const [messageUnlimited, setMessageUnlimited] = React.useState(false);
    const [agentTrainingUnlimited, setAgentTrainingUnlimited] = React.useState(false);
    const [isFreeOfCost, setIsFreeOfCost] = React.useState(false);
    
    // Re-sync unlimited states when dialog opens or initialLimits change
    useEffect(() => {
        if (open) {
            // If no plan selected, default to all unlimited and free of cost
            if (noPlanSelected) {
                setUserUnlimited(true);
                setMessageUnlimited(true);
                setAgentTrainingUnlimited(true);
                setIsFreeOfCost(true);
            } else {
                setUserUnlimited(initialLimits.userLimit === null);
                setMessageUnlimited(initialLimits.messageLimit === null);
                setAgentTrainingUnlimited(initialLimits.agentLimit === null);
                setIsFreeOfCost(priceValue === 0 || priceValue === "0" || priceValue === "0.00");
            }
        }
    }, [open, initialLimits, priceValue, noPlanSelected]);

    const formik = useFormik({
        initialValues: {
            price: priceValue ?? "",
            userLimit: initialLimits.userLimit ?? "",
            messageLimit: initialLimits.messageLimit ?? "",
            agentLimit: initialLimits.agentLimit ?? "",
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: (values, { setFieldError, setSubmitting }) => {
            let hasError = false;
            if (!userUnlimited && (values.userLimit === "" || values.userLimit === null || values.userLimit === undefined)) {
                setFieldError("userLimit", VALIDATION.REQUIRED_FIELD("User limit"));
                hasError = true;
            }
            if (!messageUnlimited && (values.messageLimit === "" || values.messageLimit === null || values.messageLimit === undefined)) {
                setFieldError("messageLimit", VALIDATION.REQUIRED_FIELD("Message limit"));
                hasError = true;
            }
            if (!agentTrainingUnlimited && (values.agentLimit === "" || values.agentLimit === null || values.agentLimit === undefined)) {
                setFieldError("agentLimit", VALIDATION.REQUIRED_FIELD("Agent limit"));
                hasError = true;
            }
            if (hasError) {
                setSubmitting(false);
                return;
            }
            const normalized = {
                ...values,
                price: isFreeOfCost ? 0 : Number(values.price) || 0,
                userLimit: userUnlimited
                    ? null
                    : values.userLimit === ""
                    ? null
                    : Number(values.userLimit),
                messageLimit: messageUnlimited
                    ? null
                    : values.messageLimit === ""
                    ? null
                    : Number(values.messageLimit),
                agentLimit: agentTrainingUnlimited
                    ? null
                    : values.agentLimit === ""
                    ? null
                    : Number(values.agentLimit),
            };
            onSave && onSave(normalized);
            onClose && onClose();
        },
    });

    const handleNumberChange = (e) => {
        formik.handleChange(e);
        formik.setFieldTouched(e.target.name, true, false);
    };

    const handleLimitInput = (e) => {
        const value = e.target.value;
        const cleanValue = value.replace(/[^0-9]/g, '').slice(0, 6);
        e.target.value = cleanValue;
    };

    const handleKeyDown = (e) => {
        if (e.key === '.' || e.key === '-' || e.key === 'e' || e.key === 'E') {
            e.preventDefault();
        }
    };

    const handlePriceBlur = (e) => {
        formik.handleBlur(e);
        const val = e.target.value;
        if (val === "" || Number(val) === 0) {
            setIsFreeOfCost(true);
            formik.setFieldValue("price", 0);
        }
    };

    // const formatPrice = (value) => {
    //     if (value === "" || value === null || value === undefined) return "";
    //     const num = Number(value);
    //     return isNaN(num) ? "" : num.toFixed(2);
    // };

    const SectionHeader = ({ title }) => (
        <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: "primary.main", mb: 2 }}
        >
            {title}
        </Typography>
    );

    return (
        <Dialog 
            open={!!open} 
            onClose={onClose} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                }
            }}
        >
            <DialogTitle
                className="text-bg-primary"
                sx={{
                    py: 2.5,
                    px: 3,
                    fontSize: "1.25rem",
                    fontWeight: 600,
                }}
            >
                Edit Custom Plan - {billingCycleLabel === "monthly" ? "Monthly" : billingCycleLabel === "yearly" ? "Yearly" : "Billing"}
            </DialogTitle>
            
            <form onSubmit={formik.handleSubmit}>
                <DialogContent sx={{ px: 3, py: 3 }}>
                    {/* Pricing Section */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            mb: 3,
                            bgcolor: "grey.50",
                            border: "1px solid",
                            borderColor: "grey.200",
                            borderRadius: 2,
                        }}
                    >
                        <SectionHeader title="Pricing" />
                        <Grid container spacing={2.5} alignItems="flex-start">
                            <Grid item xs={12} sm={8}>
                                <TextField
                                    label={`Price (${currencySymbol || ""})`}
                                    name="price"
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={isFreeOfCost ? "0.00" : formik.values.price}
                                    onChange={handleNumberChange}
                                    onBlur={handlePriceBlur}
                                    inputProps={{ step: "0.01", min: 0 }}
                                    placeholder="Enter price"
                                    disabled={isFreeOfCost}
                                    error={formik.touched.price && Boolean(formik.errors.price)}
                                    helperText={formik.touched.price && formik.errors.price}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 1,
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isFreeOfCost}
                                            onChange={(e) => {
                                                setIsFreeOfCost(e.target.checked);
                                                if (e.target.checked) {
                                                    formik.setFieldValue("price", 0);
                                                } else {
                                                    // Restore plan's default price when unchecking
                                                    formik.setFieldValue("price", planDefaultPrice ?? "");
                                                }
                                            }}
                                            size="small"
                                        />
                                    }
                                    label="Free of Cost"
                                    sx={{ mt: 0.5 }}
                                />
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Limits Section */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            bgcolor: "grey.50",
                            border: "1px solid",
                            borderColor: "grey.200",
                            borderRadius: 2,
                        }}
                    >
                        <SectionHeader title="Usage Limits" />
                        <Grid container spacing={2.5}>
                            {/* User Limit */}
                            <Grid item xs={12} sm={4}>
                                <Typography 
                                    variant="subtitle2" 
                                    sx={{ mb: 1, fontWeight: 600 }}
                                >
                                    User Limit
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={userUnlimited}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                setUserUnlimited(checked);
                                                if (checked) {
                                                    formik.setFieldValue("userLimit", "");
                                                    formik.setFieldTouched("userLimit", false);
                                                }
                                            }}
                                            size="small"
                                        />
                                    }
                                    label="Unlimited"
                                    sx={{ mb: 1 }}
                                />
                                {!userUnlimited && (
                                    <TextField
                                        fullWidth
                                        name="userLimit"
                                        size="small"
                                        value={formik.values.userLimit}
                                        onChange={handleNumberChange}
                                        onBlur={formik.handleBlur}
                                        onInput={handleLimitInput}
                                        onKeyDown={handleKeyDown}
                                        type="number"
                                        inputProps={{ 
                                            min: 0, 
                                            max: 999999,
                                            step: 1,
                                        }}
                                        placeholder="Enter user limit"
                                        error={formik.touched.userLimit && Boolean(formik.errors.userLimit)}
                                        helperText={formik.touched.userLimit && formik.errors.userLimit}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 1,
                                            },
                                        }}
                                    />
                                )}
                            </Grid>

                            {/* Message Limit */}
                            <Grid item xs={12} sm={4}>
                                <Typography 
                                    variant="subtitle2" 
                                    sx={{ mb: 1, fontWeight: 600 }}
                                >
                                    Message Limit
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={messageUnlimited}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                setMessageUnlimited(checked);
                                                if (checked) {
                                                    formik.setFieldValue("messageLimit", "");
                                                    formik.setFieldTouched("messageLimit", false);
                                                }
                                            }}
                                            size="small"
                                        />
                                    }
                                    label="Unlimited"
                                    sx={{ mb: 1 }}
                                />
                                {!messageUnlimited && (
                                    <TextField
                                        fullWidth
                                        name="messageLimit"
                                        size="small"
                                        value={formik.values.messageLimit}
                                        onChange={handleNumberChange}
                                        onBlur={formik.handleBlur}
                                        onInput={handleLimitInput}
                                        onKeyDown={handleKeyDown}
                                        type="number"
                                        inputProps={{ 
                                            min: 0, 
                                            max: 999999,
                                            step: 1,
                                        }}
                                        placeholder="Enter message limit"
                                        error={formik.touched.messageLimit && Boolean(formik.errors.messageLimit)}
                                        helperText={formik.touched.messageLimit && formik.errors.messageLimit}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 1,
                                            },
                                        }}
                                    />
                                )}
                            </Grid>

                            {/* Agent Training Limit */}
                            <Grid item xs={12} sm={4}>
                                <Typography 
                                    variant="subtitle2" 
                                    sx={{ mb: 1, fontWeight: 600 }}
                                >
                                    Agent Training Limit
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={agentTrainingUnlimited}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                setAgentTrainingUnlimited(checked);
                                                if (checked) {
                                                    formik.setFieldValue("agentLimit", "");
                                                    formik.setFieldTouched("agentLimit", false);
                                                }
                                            }}
                                            size="small"
                                        />
                                    }
                                    label="Unlimited"
                                    sx={{ mb: 1 }}
                                />
                                {!agentTrainingUnlimited && (
                                    <TextField
                                        fullWidth
                                        name="agentLimit"
                                        size="small"
                                        value={formik.values.agentLimit}
                                        onChange={handleNumberChange}
                                        onBlur={formik.handleBlur}
                                        onInput={handleLimitInput}
                                        onKeyDown={handleKeyDown}
                                        type="number"
                                        inputProps={{ 
                                            min: 0, 
                                            max: 999999,
                                            step: 1,
                                        }}
                                        placeholder="Enter agent training limit"
                                        error={formik.touched.agentLimit && Boolean(formik.errors.agentLimit)}
                                        helperText={formik.touched.agentLimit && formik.errors.agentLimit}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 1,
                                            },
                                        }}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </Paper>
                </DialogContent>
                
                <DialogActions className="border-top p-7 d-flex justify-content-between">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn btn-sm btn-outline btn-outline-primary"
                    >
                        Cancel
                    </button>

                    <button
                        type="submit"
                        className="btn btn-sm btn-primary"
                    >
                        Save
                    </button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default CustomPlanPopup;
